import React, { useState, useEffect } from 'react';
import { TabsContent, Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardHeader, CardContent, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Download, Filter, X, FilterX, Calendar, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { TableContainer } from '../components/ui/table-container';
import { fetchActivityLogs, fetchSystemLogs, exportActivityLogsCSV, exportSystemLogsCSV, LogFilters, LogDateGroup, ActivityLog, SystemLog } from '../services/logService';
import { useTheme } from '../contexts/ThemeContext';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { fetchRounds } from '../services/roundService';
import { fetchWithAuth } from '../utils/api';
import { UserAvatar } from '../components/UserAvatar';
import { fetchUsers } from '../services/userService';
import { Line, Bar, Pie } from 'react-chartjs-2';
import Chart from 'chart.js/auto';
import { getAvatarForUser } from '../utils/avatarUtils';
import './AdminLogsPanel.css';

// Common type that works for both log types
type AnyLogDateGroup = LogDateGroup<ActivityLog> | LogDateGroup<SystemLog>;

// Predefined mock data - moved outside to ensure it's always available
const MOCK_USERS = [
  { id: 1, username: 'admin' },
  { id: 2, username: 'debater1' },
  { id: 3, username: 'adjudicator1' },
];

const MOCK_ROUNDS = [
  { id: 1, round_code: 'R1001', motion: { id: 1, text: 'This house believes...' } },
  { id: 2, round_code: 'R1002', motion: { id: 2, text: 'This house would...' } },
];

const MOCK_MOTIONS = [
  { id: 1, text: 'This house believes...' },
  { id: 2, text: 'This house would...' },
];

// Mock motion service if not available
const fetchMotions = async () => {
  try {
    // First try the actual service if it exists
    try {
      const motionServiceModule = await import('../services/motionService');
      if (motionServiceModule?.fetchMotions) {
        const result = await motionServiceModule.fetchMotions();
        if (Array.isArray(result) && result.length > 0) {
          return result;
        }
      }
    } catch (err) {
      console.log("Falling back to mock motion data due to error:", err);
    }
    
    // Always return mock data if service fails or returns empty
    console.log("Using mock motion data");
    return MOCK_MOTIONS;
  } catch (error) {
    console.error("Error fetching motions:", error);
    return MOCK_MOTIONS;
  }
};

interface User {
  id: number;
  username: string;
}

interface Round {
  id: number;
  round_code: string;
  motion?: {
    id: number;
    text: string;
  };
  status?: string; // Added for completed rounds
  completed_time?: string; // Added for completed rounds
  moderator?: {
    id: number;
    username: string;
  };
  completed?: boolean; // Added for completed rounds
  created_at?: string; // Added for completed rounds
}

interface Motion {
  id: number;
  text: string;
}

const preloadAvatarUrls = async (usernames: string[]): Promise<Record<string, string>> => {
  const urlMap: Record<string, string> = {};
  await Promise.all(usernames.map(async (username) => {
    const url = await getAvatarForUser(username);
    urlMap[username] = url || '';
  }));
  return urlMap;
};

// Helper to format seconds as M:SS
function formatSecondsToMinutes(seconds: number): string {
  if (isNaN(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Add type guard helpers at the top:
function isActivityLog(log: any): log is ActivityLog {
  return log && 'round' in log && 'role' in log && 'action' in log;
}
function isSystemLog(log: any): log is SystemLog {
  return log && 'log_type' in log && 'message' in log && 'activity' in log;
}

const AdminLogsPanel: React.FC = () => {
  const navigate = useNavigate();
  const { pageTransition } = useTheme();
  const [activeTab, setActiveTab] = useState("attendance");
  const [logs, setLogs] = useState<AnyLogDateGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  // For filtering
  const [users, setUsers] = useState<User[]>(MOCK_USERS);
  const [rounds, setRounds] = useState<Round[]>(MOCK_ROUNDS);
  const [motions, setMotions] = useState<Motion[]>(MOCK_MOTIONS);
  const [filters, setFilters] = useState<LogFilters>({});
  
  // Round Logs state
  const [roundLogs, setRoundLogs] = useState<Round[]>([]);
  const [roundLogsLoading, setRoundLogsLoading] = useState(false);
  const [selectedRound, setSelectedRound] = useState<Round | null>(null);
  const [roundDetail, setRoundDetail] = useState<any>(null);
  const [roundDetailLoading, setRoundDetailLoading] = useState(false);
  const [roundDetailError, setRoundDetailError] = useState('');

  // Performance Logs state
  const [usersList, setUsersList] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userPerfStats, setUserPerfStats] = useState<any>(null);
  const [userPerfLoading, setUserPerfLoading] = useState(false);
  const [userPerfError, setUserPerfError] = useState('');
  const [perfTab, setPerfTab] = useState<'debater' | 'adjudicator' | 'total'>('debater');
  const [totalComputeStats, setTotalComputeStats] = useState<any[]>([]);
  const [totalComputeLoading, setTotalComputeLoading] = useState(false);

  // Avatar URL maps for charts
  const [speakerAvatarMap, setSpeakerAvatarMap] = useState<Record<string, string>>({});
  const [adjudicatorAvatarMap, setAdjudicatorAvatarMap] = useState<Record<string, string>>({});

  // Add state for avatar URLs for Total Compute
  const [totalComputeAvatarMap, setTotalComputeAvatarMap] = useState<Record<string, string>>({});

  // 1. Add pagination state for each tab:
  const [attendancePage, setAttendancePage] = useState(1);
  const [systemPage, setSystemPage] = useState(1);
  const [roundLogsPage, setRoundLogsPage] = useState(1);
  const [performancePage, setPerformancePage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // 1. Add separate pagination state for day groups:
  const [attendanceDayPage, setAttendanceDayPage] = useState(1);
  const [systemDayPage, setSystemDayPage] = useState(1);
  const DAY_GROUPS_PER_PAGE = 2;

  // 2. Reset page to 1 when filters or tab changes:
  useEffect(() => { setAttendancePage(1); }, [filters, activeTab]);
  useEffect(() => { setSystemPage(1); }, [filters, activeTab]);
  useEffect(() => { setRoundLogsPage(1); }, [activeTab]);
  useEffect(() => { setPerformancePage(1); }, [activeTab, filters]);

  // 2. Reset day group page to 1 when filters or tab changes:
  useEffect(() => { setAttendanceDayPage(1); }, [filters, activeTab, logs]);
  useEffect(() => { setSystemDayPage(1); }, [filters, activeTab, logs]);

  // 3. Paginate data for each tab:
  const paginatedAttendanceLogs = logs.slice((attendancePage - 1) * ITEMS_PER_PAGE, attendancePage * ITEMS_PER_PAGE);
  const attendanceTotalPages = Math.ceil(logs.length / ITEMS_PER_PAGE);

  const paginatedSystemLogs = logs.slice((systemPage - 1) * ITEMS_PER_PAGE, systemPage * ITEMS_PER_PAGE);
  const systemTotalPages = Math.ceil(logs.length / ITEMS_PER_PAGE);

  const paginatedRoundLogs = roundLogs.slice((roundLogsPage - 1) * ITEMS_PER_PAGE, roundLogsPage * ITEMS_PER_PAGE);
  const roundLogsTotalPages = Math.ceil(roundLogs.length / ITEMS_PER_PAGE);

  const paginatedUsersList = usersList.slice((performancePage - 1) * ITEMS_PER_PAGE, performancePage * ITEMS_PER_PAGE);
  const usersListTotalPages = Math.ceil(usersList.length / ITEMS_PER_PAGE);

  // 3. Paginate day groups for each tab:
  const paginatedAttendanceDayGroups = paginatedAttendanceLogs.slice((attendanceDayPage - 1) * DAY_GROUPS_PER_PAGE, attendanceDayPage * DAY_GROUPS_PER_PAGE);
  const attendanceDayTotalPages = Math.ceil(paginatedAttendanceLogs.length / DAY_GROUPS_PER_PAGE);

  const paginatedSystemDayGroups = paginatedSystemLogs.slice((systemDayPage - 1) * DAY_GROUPS_PER_PAGE, systemDayPage * DAY_GROUPS_PER_PAGE);
  const systemDayTotalPages = Math.ceil(paginatedSystemLogs.length / DAY_GROUPS_PER_PAGE);

  // Load reference data
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [usersData, roundsData, motionsData] = await Promise.all([
          fetchUsers(),
          fetchRounds(),
          fetchMotions()
        ]);
        
        if (Array.isArray(usersData)) setUsers(usersData);
        if (Array.isArray(roundsData)) setRounds(roundsData);
        if (Array.isArray(motionsData)) setMotions(motionsData);
      } catch (err: any) {
        console.error("Error loading reference data:", err);
        toast.error("Failed to load filter data, using mock data instead");
      }
    };
    
    loadReferenceData();
  }, []);
  
  // Load logs when tab changes or filters change
  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        let data: AnyLogDateGroup[] = [];
        
        if (activeTab === 'attendance') {
          data = await fetchActivityLogs(filters).catch(err => {
            console.error("Error fetching activity logs:", err);
            return [];
          });
        } else {
          data = await fetchSystemLogs(filters).catch(err => {
            console.error("Error fetching system logs:", err);
            return [];
          });
        }
        
        // Ensure data is always an array
        if (!Array.isArray(data)) {
          console.warn("Logs API did not return an array, using empty array instead");
          data = [];
        }
        
        setLogs(data);
        setError('');
      } catch (err: any) {
        setError('Failed to load logs: ' + (err.message || 'Unknown error'));
        console.error("Error loading logs:", err);
        setLogs([]); // Ensure logs is set to an empty array on error
      } finally {
        setLoading(false);
      }
    };
    
    fetchLogs();
  }, [activeTab, filters]);

  // Fetch completed rounds for Round Logs tab
  useEffect(() => {
    if (activeTab !== 'round_logs') return;
    setRoundLogsLoading(true);
    fetchRounds()
      .then((rounds) => {
        setRoundLogs(rounds.filter(r => r.status === 'completed' || r.completed === true));
      })
      .catch((err) => {
        setRoundLogs([]);
      })
      .finally(() => setRoundLogsLoading(false));
  }, [activeTab]);

  // Fetch round details when a round is selected
  useEffect(() => {
    if (!selectedRound) return;
    setRoundDetailLoading(true);
    setRoundDetailError('');
    const endpoints = [
      `/api/round/${selectedRound.id}/results/`,
      `/api/rounds/${selectedRound.id}/results/`,
      `/round/${selectedRound.id}/results/`
    ];
    (async () => {
      let data = null;
      for (const endpoint of endpoints) {
        try {
          data = await fetchWithAuth(endpoint);
          if (data) break;
        } catch (err) {
          // try next endpoint
        }
      }
      if (data) {
        setRoundDetail(data);
      } else {
        setRoundDetailError('Could not load round details.');
        setRoundDetail(null);
      }
      setRoundDetailLoading(false);
    })();
  }, [selectedRound]);

  // Fetch all users for Performance Logs tab
  useEffect(() => {
    if (activeTab !== 'performance_logs') return;
    setUsersLoading(true);
    fetchUsers()
      .then((users) => {
        setUsersList(users);
      })
      .catch(() => setUsersList([]))
      .finally(() => setUsersLoading(false));
  }, [activeTab]);

  // Fetch user performance stats when a user is selected (Admin endpoint)
  useEffect(() => {
    if (!selectedUser) return;
    setUserPerfLoading(true);
    setUserPerfError('');
    (async () => {
      try {
        const data = await fetchWithAuth(`/api/admin/logs/user-performance/${selectedUser.id}`); // Use admin logs endpoint
        setUserPerfStats(data);
      } catch (err) {
        setUserPerfError('Could not load user performance stats.');
        setUserPerfStats(null);
      }
      setUserPerfLoading(false);
    })();
  }, [selectedUser]);

  // Fetch and aggregate total compute stats for all users (Admin endpoint)
  useEffect(() => {
    if (activeTab !== 'performance_logs' || perfTab !== 'total') return;
    setTotalComputeLoading(true);
    (async () => {
      try {
        const data = await fetchWithAuth('/api/admin/logs/total-compute'); // Use admin logs endpoint
        // data should be an array of { user, stats }
        setTotalComputeStats(data || []);
      } catch (err) {
        setTotalComputeStats([]);
      }
      setTotalComputeLoading(false);
    })();
  }, [activeTab, perfTab]);
  
  // Preload avatar URLs for speaker scores when roundDetail changes
  useEffect(() => {
    if (roundDetail && roundDetail.speaker_scores && roundDetail.speaker_scores.length > 0) {
      const usernames = roundDetail.speaker_scores.map((s: any) => s.username);
      preloadAvatarUrls(usernames).then(setSpeakerAvatarMap);
    } else {
      setSpeakerAvatarMap({});
    }
  }, [roundDetail && roundDetail.speaker_scores]);

  // Preload avatar URLs for adjudicator ratings when roundDetail changes
  useEffect(() => {
    if (roundDetail && roundDetail.adjudicator_ratings && roundDetail.adjudicator_ratings.length > 0) {
      const usernames = roundDetail.adjudicator_ratings.map((a: any) => a.username);
      preloadAvatarUrls(usernames).then(setAdjudicatorAvatarMap);
    } else {
      setAdjudicatorAvatarMap({});
    }
  }, [roundDetail && roundDetail.adjudicator_ratings]);
  
  // Preload avatar URLs for all users in Total Compute when stats are loaded
  useEffect(() => {
    if (perfTab === 'total' && totalComputeStats.length > 0) {
      const usernames = totalComputeStats.filter(e => e && e.username && e.debate_stats && e.adjudication_stats).map(e => e.username);
      preloadAvatarUrls(usernames).then(setTotalComputeAvatarMap);
    } else {
      setTotalComputeAvatarMap({});
    }
  }, [perfTab, totalComputeStats]);
  
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    // Reset filters when changing tabs
    setFilters({});
  };
  
  const handleExportCSV = async () => {
    if (logs.length === 0) {
      toast.error("No logs to export");
      return;
    }
    
    setExporting(true);
    
    try {
      if (activeTab === 'attendance') {
        await exportActivityLogsCSV(filters);
      } else {
        await exportSystemLogsCSV(filters);
      }
      toast.success("Logs exported successfully");
    } catch (err: any) {
      console.error("Export error:", err);
      toast.error(`Export failed: ${err.message || 'Unknown error'}`);
    } finally {
      setExporting(false);
    }
  };
  
  const handleFilterChange = (name: keyof LogFilters, value: any) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  
  const clearFilters = () => {
    setFilters({});
  };
  
  const getTotalLogCount = (): number => {
    if (!Array.isArray(logs)) return 0;
    return logs.reduce((total, day) => {
      if (!day || !Array.isArray(day.logs)) return total;
      return total + day.logs.length;
    }, 0);
  };
  
  const getStatusColor = (logType: string) => {
    switch (logType) {
      case 'ERROR':
        return 'text-red-600 dark:text-red-400';
      case 'WARNING':
        return 'text-amber-600 dark:text-amber-400';
      case 'INFO':
        return 'text-blue-600 dark:text-blue-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };
  
  const renderFilterPanel = () => {
    if (activeTab === 'attendance') {
      // Filters for attendance logs
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <Label htmlFor="user_filter">User</Label>
            <Select
              value={filters.user_id?.toString() || ''}
              onValueChange={(value) => handleFilterChange('user_id', value ? parseInt(value) : undefined)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select user" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="">All Users</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id.toString()}>
                      {user.username}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="round_filter">Round</Label>
            <Select
              value={filters.round_id?.toString() || ''}
              onValueChange={(value) => handleFilterChange('round_id', value ? parseInt(value) : undefined)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select round" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="">All Rounds</SelectItem>
                  {rounds.map(round => (
                    <SelectItem key={round.id} value={round.id.toString()}>
                      Round {round.id} ({round.round_code})
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="motion_filter">Motion</Label>
            <Select
              value={filters.motion_id?.toString() || ''}
              onValueChange={(value) => handleFilterChange('motion_id', value ? parseInt(value) : undefined)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select motion" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="">All Motions</SelectItem>
                  {motions.map(motion => (
                    <SelectItem key={motion.id} value={motion.id.toString()}>
                      {motion.text.length > 40 ? motion.text.substring(0, 40) + '...' : motion.text}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="role_filter">Role</Label>
            <Select
              value={filters.role || ''}
              onValueChange={(value) => handleFilterChange('role', value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="">All Roles</SelectItem>
                  <SelectItem value="Speaker">Speaker</SelectItem>
                  <SelectItem value="Adjudicator">Adjudicator</SelectItem>
                  <SelectItem value="Observer">Observer</SelectItem>
                  <SelectItem value="Moderator">Moderator</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="action_filter">Action</Label>
            <Select
              value={filters.action || ''}
              onValueChange={(value) => handleFilterChange('action', value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select action" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="">All Actions</SelectItem>
                  <SelectItem value="joined">Joined Round</SelectItem>
                  <SelectItem value="allocated">Allocated</SelectItem>
                  <SelectItem value="completed">Completed Round</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          
          <div className="col-span-1 md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date_from">Date From</Label>
              <Input
                id="date_from"
                type="date"
                value={filters.date_from || ''}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <Label htmlFor="date_to">Date To</Label>
              <Input
                id="date_to"
                type="date"
                value={filters.date_to || ''}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="w-full"
              />
            </div>
          </div>
          
          <div className="col-span-1 md:col-span-3 flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={clearFilters}
              className="flex items-center gap-1"
            >
              <FilterX className="h-4 w-4" />
              Clear Filters
            </Button>
            
            <Button
              variant="default"
              onClick={() => setIsFilterOpen(false)}
              className="flex items-center gap-1"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      );
    } else {
      // Filters for system logs
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <Label htmlFor="log_type_filter">Log Type</Label>
            <Select
              value={filters.log_type || ''}
              onValueChange={(value) => handleFilterChange('log_type', value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select log type" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="">All Types</SelectItem>
                  <SelectItem value="INFO">Information</SelectItem>
                  <SelectItem value="WARNING">Warning</SelectItem>
                  <SelectItem value="ERROR">Error</SelectItem>
                  <SelectItem value="ACTION">User Action</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="user_filter">User</Label>
            <Select
              value={filters.user_id?.toString() || ''}
              onValueChange={(value) => handleFilterChange('user_id', value ? parseInt(value) : undefined)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select user" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="">All Users</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id.toString()}>
                      {user.username}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="activity_filter">Activity</Label>
            <Input
              id="activity_filter"
              type="text"
              placeholder="Filter by activity"
              value={filters.activity || ''}
              onChange={(e) => handleFilterChange('activity', e.target.value)}
              className="w-full"
            />
          </div>
          
          <div className="col-span-1 md:col-span-3">
            <Label htmlFor="message_filter">Message</Label>
            <Input
              id="message_filter"
              type="text"
              placeholder="Search in messages"
              value={filters.message || ''}
              onChange={(e) => handleFilterChange('message', e.target.value)}
              className="w-full"
            />
          </div>
          
          <div className="col-span-1 md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date_from">Date From</Label>
              <Input
                id="date_from"
                type="date"
                value={filters.date_from || ''}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <Label htmlFor="date_to">Date To</Label>
              <Input
                id="date_to"
                type="date"
                value={filters.date_to || ''}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
                className="w-full"
              />
            </div>
          </div>
          
          <div className="col-span-1 md:col-span-3 flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={clearFilters}
              className="flex items-center gap-1"
            >
              <FilterX className="h-4 w-4" />
              Clear Filters
            </Button>
            
            <Button
              variant="default"
              onClick={() => setIsFilterOpen(false)}
              className="flex items-center gap-1"
            >
              Apply Filters
            </Button>
          </div>
        </div>
      );
    }
  };
  
  const renderAttendanceLogs = () => {
    if (!Array.isArray(logs) || logs.length === 0) {
      return (
        <div className="p-4 text-center text-gray-500 dark:text-gray-400">
          No attendance logs found{Object.keys(filters).length > 0 ? ' matching your filters' : ''}
        </div>
      );
    }
    
    // Filter only ActivityLog entries using type guard
    const activityLogs = logs.filter((item): item is LogDateGroup<ActivityLog> => {
      if (!item || !item.logs || !Array.isArray(item.logs) || item.logs.length === 0) return false;
      // Check if the first log has ActivityLog properties
      const firstLog = item.logs[0];
      return firstLog && 'round' in firstLog && 'role' in firstLog && 'action' in firstLog;
    });
    
    return (
      <div className="space-y-6">
        {activityLogs.map((dayGroup) => (
          <Card className="admin-logs-card mb-6 p-0 border dark:border-gray-700 rounded-lg overflow-hidden" key={dayGroup.date}>
            <div className="bg-gray-100 dark:bg-gray-700 p-3 font-medium flex justify-between items-center">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-2" />
                {format(new Date(dayGroup.date), 'MMMM d, yyyy')}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {dayGroup.logs.length} {dayGroup.logs.length === 1 ? 'entry' : 'entries'}
              </div>
            </div>
            
            <TableContainer>
              <div className="admin-logs-table overflow-x-auto mb-6">
                <table className="min-w-[600px] w-full border-collapse">
                  <thead>
                    <tr style={{ backgroundColor: 'var(--secondary-bg)' }}>
                      <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">User</th>
                      <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Round</th>
                      <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Role</th>
                      <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Action</th>
                      <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(dayGroup.logs) && dayGroup.logs.length > 0 && isActivityLog(dayGroup.logs[0])
                      ? dayGroup.logs.map((log) => {
                          const aLog = log as ActivityLog;
                          return (
                            <tr key={aLog.id} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750">
                              <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">{aLog.user?.username}</td>
                              <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">
                                {aLog.round ? `Round ${aLog.round.id}${aLog.round.motion ? `: ${aLog.round.motion.text.substring(0, 30)}...` : ''}` : 'N/A'}
                              </td>
                              <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">{aLog.role}</td>
                              <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">{aLog.action}</td>
                              <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">
                                {format(new Date(aLog.timestamp), 'h:mm a')}
                              </td>
                            </tr>
                          );
                        })
                      : null}
                  </tbody>
                </table>
              </div>
            </TableContainer>
          </Card>
        ))}
      </div>
    );
  };
  
  const renderSystemLogs = () => {
    if (!Array.isArray(logs) || logs.length === 0) {
      return (
        <div className="p-4 text-center text-gray-500 dark:text-gray-400">
          No system logs found{Object.keys(filters).length > 0 ? ' matching your filters' : ''}
        </div>
      );
    }
    
    // Filter only SystemLog entries using type guard
    const systemLogs = logs.filter((item): item is LogDateGroup<SystemLog> => {
      if (!item || !item.logs || !Array.isArray(item.logs) || item.logs.length === 0) return false;
      // Check if the first log has SystemLog properties
      const firstLog = item.logs[0];
      return firstLog && 'log_type' in firstLog && 'message' in firstLog && 'activity' in firstLog;
    });
    
    return (
      <div className="space-y-6">
        {systemLogs.map((dayGroup) => (
          <Card className="admin-logs-card mb-6 p-0 border dark:border-gray-700 rounded-lg overflow-hidden" key={dayGroup.date}>
            <div className="bg-gray-100 dark:bg-gray-700 p-3 font-medium flex justify-between items-center">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-2" />
                {format(new Date(dayGroup.date), 'MMMM d, yyyy')}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {dayGroup.logs.length} {dayGroup.logs.length === 1 ? 'entry' : 'entries'}
              </div>
            </div>
            
            <TableContainer>
              <div className="admin-logs-table overflow-x-auto mb-6">
                <table className="min-w-[600px] w-full border-collapse">
                  <thead>
                    <tr style={{ backgroundColor: 'var(--secondary-bg)' }}>
                      <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Type</th>
                      <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Message</th>
                      <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">User</th>
                      <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Activity</th>
                      <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(dayGroup.logs) && dayGroup.logs.length > 0 && isSystemLog(dayGroup.logs[0])
                      ? dayGroup.logs.map((log) => {
                          const sLog = log as SystemLog;
                          return (
                            <tr key={sLog.id} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750">
                              <td className={`p-2 text-sm font-medium ${getStatusColor(sLog.log_type)}`}>{sLog.log_type}</td>
                              <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">{sLog.message}</td>
                              <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">{sLog.user?.username || 'System'}</td>
                              <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">{sLog.activity}</td>
                              <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">
                                {format(new Date(sLog.timestamp), 'h:mm a')}
                              </td>
                            </tr>
                          );
                        })
                      : null}
                  </tbody>
                </table>
              </div>
            </TableContainer>
          </Card>
        ))}
      </div>
    );
  };
  
  return (
    <div className="admin-logs-main px-2 sm:px-4 md:px-8 w-full">
      <div className="admin-logs-toolbar flex flex-col sm:flex-row flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center">
          <Button 
            variant="ghost" 
            className="mr-2" 
            onClick={() => pageTransition(() => navigate('/dashboard'))}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold mb-4 dark:text-white light:text-gray-800">
            Activity Logs
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="flex items-center gap-1"
          >
            {isFilterOpen ? <X className="h-4 w-4" /> : <Filter className="h-4 w-4" />}
            {isFilterOpen ? 'Hide Filters' : 'Show Filters'}
          </Button>
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setFilters({})}
            disabled={loading}
            className="flex items-center gap-1"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          <Button 
            variant="secondary" 
            size="sm"
            onClick={handleExportCSV}
            disabled={logs.length === 0 || loading || exporting}
            className="flex items-center gap-1"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>
      
      {/* Filter Panel */}
      <Collapsible open={isFilterOpen} onOpenChange={setIsFilterOpen} className="mb-6">
        <CollapsibleContent>
          <Card className="shadow-lg rounded-lg bg-card mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center">
                <Filter className="h-4 w-4 mr-2" />
                Filter Logs
                {Object.keys(filters).length > 0 && (
                  <span className="ml-2 text-sm bg-blue-500 text-white rounded-full px-2 py-0.5">
                    {Object.keys(filters).length}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {renderFilterPanel()}
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
      
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid grid-cols-4 mb-8 max-w-2xl">
          <TabsTrigger value="attendance">Attendance Logs</TabsTrigger>
          <TabsTrigger value="system">System Logs</TabsTrigger>
          <TabsTrigger value="round_logs">Round Logs</TabsTrigger>
          <TabsTrigger value="performance_logs">Performance Logs</TabsTrigger>
        </TabsList>
        
        <TabsContent value="attendance">
          <Card className="shadow-lg rounded-lg bg-card mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="dark:text-white light:text-gray-800">
                Participant Attendance Records
                {getTotalLogCount() > 0 && (
                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                    ({getTotalLogCount()} entries)
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : error ? (
                <div className="p-4 bg-red-500 bg-opacity-10 border border-red-500 rounded-lg">
                  <p className="text-red-500">{error}</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {paginatedAttendanceDayGroups.map((dayGroup) => (
                    <Card className="admin-logs-card mb-6 p-0 border dark:border-gray-700 rounded-lg overflow-hidden" key={dayGroup.date}>
                      <div className="bg-gray-100 dark:bg-gray-700 p-3 font-medium flex justify-between items-center">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2" />
                          {format(new Date(dayGroup.date), 'MMMM d, yyyy')}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {dayGroup.logs.length} {dayGroup.logs.length === 1 ? 'entry' : 'entries'}
                        </div>
                      </div>
                      
                      <TableContainer>
                        <div className="admin-logs-table overflow-x-auto mb-6">
                          <table className="min-w-[600px] w-full border-collapse">
                            <thead>
                              <tr style={{ backgroundColor: 'var(--secondary-bg)' }}>
                                <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">User</th>
                                <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Round</th>
                                <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Role</th>
                                <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Action</th>
                                <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Time</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Array.isArray(dayGroup.logs) && dayGroup.logs.length > 0 && isActivityLog(dayGroup.logs[0])
                                ? dayGroup.logs.map((log) => {
                                    const aLog = log as ActivityLog;
                                    return (
                                      <tr key={aLog.id} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750">
                                        <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">{aLog.user?.username}</td>
                                        <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">
                                          {aLog.round ? `Round ${aLog.round.id}${aLog.round.motion ? `: ${aLog.round.motion.text.substring(0, 30)}...` : ''}` : 'N/A'}
                                        </td>
                                        <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">{aLog.role}</td>
                                        <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">{aLog.action}</td>
                                        <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">
                                          {format(new Date(aLog.timestamp), 'h:mm a')}
                                        </td>
                                      </tr>
                                    );
                                  })
                                : null}
                            </tbody>
                          </table>
                        </div>
                      </TableContainer>
                    </Card>
                  ))}
                  {attendanceDayTotalPages > 1 && (
                    <div className="flex justify-center items-center gap-2 mt-6">
                      <Button
                        variant="outline"
                        onClick={() => setAttendanceDayPage(prev => Math.max(1, prev - 1))}
                        disabled={attendanceDayPage === 1}
                        className="flex items-center gap-1"
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Page {attendanceDayPage} of {attendanceDayTotalPages}
                      </span>
                      <Button
                        variant="outline"
                        onClick={() => setAttendanceDayPage(prev => Math.min(attendanceDayTotalPages, prev + 1))}
                        disabled={attendanceDayPage === attendanceDayTotalPages}
                        className="flex items-center gap-1"
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              )}
              
              <p className="text-xs text-gray-500 mt-6">
                Note: These logs track user participation across rounds and are used for attendance verification.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="system">
          <Card className="shadow-lg rounded-lg bg-card mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="dark:text-white light:text-gray-800">
                System Activity Logs
                {getTotalLogCount() > 0 && (
                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                    ({getTotalLogCount()} entries)
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : error ? (
                <div className="p-4 bg-red-500 bg-opacity-10 border border-red-500 rounded-lg">
                  <p className="text-red-500">{error}</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {paginatedSystemDayGroups.map((dayGroup) => (
                    <Card className="admin-logs-card mb-6 p-0 border dark:border-gray-700 rounded-lg overflow-hidden" key={dayGroup.date}>
                      <div className="bg-gray-100 dark:bg-gray-700 p-3 font-medium flex justify-between items-center">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2" />
                          {format(new Date(dayGroup.date), 'MMMM d, yyyy')}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {dayGroup.logs.length} {dayGroup.logs.length === 1 ? 'entry' : 'entries'}
                        </div>
                      </div>
                      
                      <TableContainer>
                        <div className="admin-logs-table overflow-x-auto mb-6">
                          <table className="min-w-[600px] w-full border-collapse">
                            <thead>
                              <tr style={{ backgroundColor: 'var(--secondary-bg)' }}>
                                <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Type</th>
                                <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Message</th>
                                <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">User</th>
                                <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Activity</th>
                                <th className="p-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300">Time</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Array.isArray(dayGroup.logs) && dayGroup.logs.length > 0 && isSystemLog(dayGroup.logs[0])
                                ? dayGroup.logs.map((log) => {
                                    const sLog = log as SystemLog;
                                    return (
                                      <tr key={sLog.id} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750">
                                        <td className={`p-2 text-sm font-medium ${getStatusColor(sLog.log_type)}`}>{sLog.log_type}</td>
                                        <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">{sLog.message}</td>
                                        <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">{sLog.user?.username || 'System'}</td>
                                        <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">{sLog.activity}</td>
                                        <td className="p-2 text-sm dark:text-gray-300 light:text-gray-700">
                                          {format(new Date(sLog.timestamp), 'h:mm a')}
                                        </td>
                                      </tr>
                                    );
                                  })
                                : null}
                            </tbody>
                          </table>
                        </div>
                      </TableContainer>
                    </Card>
                  ))}
                  {systemDayTotalPages > 1 && (
                    <div className="flex justify-center items-center gap-2 mt-6">
                      <Button
                        variant="outline"
                        onClick={() => setSystemDayPage(prev => Math.max(1, prev - 1))}
                        disabled={systemDayPage === 1}
                        className="flex items-center gap-1"
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Page {systemDayPage} of {systemDayTotalPages}
                      </span>
                      <Button
                        variant="outline"
                        onClick={() => setSystemDayPage(prev => Math.min(systemDayTotalPages, prev + 1))}
                        disabled={systemDayPage === systemDayTotalPages}
                        className="flex items-center gap-1"
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              )}
              
              <p className="text-xs text-gray-500 mt-6">
                Note: System logs track important events and actions in the application for troubleshooting and auditing purposes.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="round_logs">
          <Card className="shadow-lg rounded-lg bg-card mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="dark:text-white light:text-gray-800">
                Round Logs
              </CardTitle>
            </CardHeader>
            <CardContent>
              {roundLogsLoading ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">Loading completed rounds...</div>
              ) : roundLogs.length === 0 ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">No completed rounds found.</div>
              ) : selectedRound && roundDetail ? (
                <div>
                  <Button variant="outline" onClick={() => setSelectedRound(null)} className="mb-4">Back to Rounds List</Button>
                  <h2 className="admin-logs-heading text-lg sm:text-xl font-semibold mb-4">Round {selectedRound.round_code}</h2>
                  <div className="mb-2">Motion: <span className="font-semibold">{roundDetail.motion}</span></div>
                  <div className="mb-2">Winning Side: <span className="font-semibold">{roundDetail.winning_side || 'N/A'}</span></div>
                  <div className="mb-2">Summary: <span className="font-semibold">{roundDetail.summary || 'N/A'}</span></div>
                  <div className="mb-2">Completed At: <span className="font-semibold">{roundDetail.completed_at || selectedRound.completed_time || 'N/A'}</span></div>
                  <div className="mb-4">Info Slide: <span className="font-semibold">{roundDetail.info_slide || 'N/A'}</span></div>
                  <h3 className="text-lg font-semibold mt-4 mb-2">Speaker Scores</h3>
                  <div className="overflow-x-auto mb-4">
                    <table className="min-w-[600px] w-full text-sm">
                      <thead>
                        <tr>
                          <th className="p-2 text-left">User</th>
                          <th className="p-2 text-left">Role</th>
                          <th className="p-2 text-left">Score</th>
                          <th className="p-2 text-left">Comments</th>
                          <th className="p-2 text-left">Speaking Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {roundDetail.speaker_scores && roundDetail.speaker_scores.length > 0 ? roundDetail.speaker_scores.map((score: any) => (
                          <tr key={score.user_id}>
                            <td className="p-2 flex items-center gap-2"><UserAvatar username={score.username} size="sm" />{score.username}</td>
                            <td className="p-2">{score.role}</td>
                            <td className="p-2">{score.score}</td>
                            <td className="p-2">{score.comments || '-'}</td>
                            <td className="p-2">{score.speaking_time || '-'}</td>
                          </tr>
                        )) : <tr><td colSpan={5} className="p-2 text-center">No speaker scores.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  <h3 className="text-lg font-semibold mt-4 mb-2">Adjudicator Ratings</h3>
                  <div className="overflow-x-auto mb-4">
                    <table className="min-w-[600px] w-full text-sm">
                      <thead>
                        <tr>
                          <th className="p-2 text-left">User</th>
                          <th className="p-2 text-left">Role</th>
                          <th className="p-2 text-left">Average Rating</th>
                        </tr>
                      </thead>
                      <tbody>
                        {roundDetail.adjudicator_ratings && roundDetail.adjudicator_ratings.length > 0 ? roundDetail.adjudicator_ratings.map((adj: any) => (
                          <tr key={adj.user_id}>
                            <td className="p-2 flex items-center gap-2"><UserAvatar username={adj.username} size="sm" />{adj.username}</td>
                            <td className="p-2">{adj.role}</td>
                            <td className="p-2">{adj.average_rating !== undefined && adj.average_rating !== null ? adj.average_rating.toFixed(2) : '-'}</td>
                          </tr>
                        )) : <tr><td colSpan={3} className="p-2 text-center">No adjudicator ratings.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  {roundDetail.speaker_scores && roundDetail.speaker_scores.length > 0 && roundDetail.adjudicator_ratings && roundDetail.adjudicator_ratings.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                      <div className="h-80 overflow-x-auto p-2 bg-transparent rounded-lg">
                        <h4 className="font-semibold mb-2">Speaker Scores (Bar Graph)</h4>
                        <Bar
                          data={{
                            labels: roundDetail.speaker_scores.map((s: any) => s.username),
                            datasets: [
                              {
                                label: 'Score',
                                data: roundDetail.speaker_scores.map((s: any) => s.score),
                                backgroundColor: 'rgba(53, 162, 235, 0.7)',
                                barThickness: 12,
                                maxBarThickness: 16,
                              },
                            ],
                          }}
                          options={{
                            plugins: {
                              tooltip: {
                                callbacks: {
                                  label: function(context: any) {
                                    const username = context.label;
                                    const avatarUrl = speakerAvatarMap[username];
                                    return [
                                      `Score: ${context.parsed.y}`,
                                      `User: ${username}`,
                                      avatarUrl ? `Avatar: ${avatarUrl}` : '',
                                    ];
                                  },
                                },
                              },
                            },
                            scales: {
                              y: {
                                min: 67,
                                max: 83,
                                ticks: { stepSize: 5 },
                                title: { display: true, text: 'Speaker Score' },
                              },
                            },
                          }}
                        />
                      </div>
                      <div className="h-80 overflow-x-auto p-2 bg-transparent rounded-lg">
                        <h4 className="font-semibold mb-2">Adjudicator Ratings (Bar Graph)</h4>
                        <Bar
                          data={{
                            labels: roundDetail.adjudicator_ratings.map((a: any) => a.username),
                            datasets: [
                              {
                                label: 'Average Rating',
                                data: roundDetail.adjudicator_ratings.map((a: any) => a.average_rating),
                                backgroundColor: 'rgba(255, 159, 64, 0.7)',
                                barThickness: 12,
                                maxBarThickness: 16,
                              },
                            ],
                          }}
                          options={{
                            plugins: {
                              tooltip: {
                                callbacks: {
                                  label: function(context: any) {
                                    const username = context.label;
                                    const avatarUrl = adjudicatorAvatarMap[username];
                                    return [
                                      `Rating: ${context.parsed.y}`,
                                      `User: ${username}`,
                                      avatarUrl ? `Avatar: ${avatarUrl}` : '',
                                    ];
                                  },
                                },
                              },
                            },
                            scales: {
                              y: {
                                min: 1,
                                max: 10,
                                ticks: { stepSize: 1 },
                                title: { display: true, text: 'Adjudicator Rating' },
                              },
                            },
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {roundDetail.speaker_scores && roundDetail.speaker_scores.length > 0 && (
                    <div className="mb-8">
                      <h4 className="font-semibold mb-2">Speaking Time (Bar Graph)</h4>
                      <Bar
                        data={{
                          labels: roundDetail.speaker_scores.map((s: any) => s.username),
                          datasets: [
                            {
                              label: 'Speaking Time (minutes)',
                              data: roundDetail.speaker_scores.map((s: any) => {
                                if (!s.speaking_time) return 0;
                                const parts = s.speaking_time.split(':');
                                if (parts.length === 2) {
                                  return parseInt(parts[0], 10) + parseInt(parts[1], 10) / 60;
                                }
                                return (parseInt(s.speaking_time, 10) || 0) / 60;
                              }),
                              backgroundColor: 'rgba(75, 192, 192, 0.7)',
                              barThickness: 12,
                              maxBarThickness: 16,
                            },
                          ],
                        }}
                        options={{
                          plugins: {
                            tooltip: {
                              callbacks: {
                                label: function(context: any) {
                                  const username = context.label;
                                  const avatarUrl = speakerAvatarMap[username];
                                  const minutes = context.parsed.y;
                                  return [
                                    `Speaking Time: ${formatSecondsToMinutes(minutes * 60)}`,
                                    `User: ${username}`,
                                    avatarUrl ? `Avatar: ${avatarUrl}` : '',
                                  ];
                                },
                              },
                            },
                          },
                          scales: {
                            y: {
                              min: 0,
                              max: 8,
                              ticks: {
                                stepSize: 0.5,
                                callback: function(tickValue: string | number) {
                                  if (typeof tickValue === 'number') {
                                    return formatSecondsToMinutes(tickValue * 60);
                                  }
                                  return tickValue;
                                }
                              },
                              title: { display: true, text: 'Speaking Time (minutes)' },
                            },
                          },
                        }}
                      />
                    </div>
                  )}
                  {/* TODO: Add comments and detailed view/graphs here */}
                </div>
              ) : selectedRound && roundDetailLoading ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">Loading round details...</div>
              ) : selectedRound && roundDetailError ? (
                <div className="p-4 text-center text-red-500">{roundDetailError}</div>
              ) : (
                <div>
                  <h2 className="admin-logs-heading text-lg font-semibold mb-4">Completed Rounds</h2>
                  <div className="overflow-x-auto">
                    <table className="min-w-[600px] w-full text-sm">
                      <thead>
                        <tr>
                          <th className="p-2 text-left">Round Number</th>
                          <th className="p-2 text-left">Round Code</th>
                          <th className="p-2 text-left">Motion</th>
                          <th className="p-2 text-left">Moderator</th>
                          <th className="p-2 text-left">Completed At</th>
                          <th className="p-2 text-left">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedRoundLogs.map(round => (
                          <tr key={round.id}>
                            <td className="p-2">{round.id}</td>
                            <td className="p-2">{round.round_code}</td>
                            <td className="p-2">{round.motion?.text || '-'}</td>
                            <td className="p-2">{round.moderator?.username || '-'}</td>
                            <td className="p-2">{round.completed_time ? format(new Date(round.completed_time), 'yyyy-MM-dd HH:mm') : (round.created_at ? format(new Date(round.created_at), 'yyyy-MM-dd HH:mm') : '-')}</td>
                            <td className="p-2">
                              <Button size="sm" variant="secondary" onClick={() => setSelectedRound(round)}>View</Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {roundLogsTotalPages > 1 && (
                    <div className="flex justify-center items-center gap-2 mt-6">
                      <Button
                        variant="outline"
                        onClick={() => setRoundLogsPage(prev => Math.max(1, prev - 1))}
                        disabled={roundLogsPage === 1}
                        className="flex items-center gap-1"
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Page {roundLogsPage} of {roundLogsTotalPages}
                      </span>
                      <Button
                        variant="outline"
                        onClick={() => setRoundLogsPage(prev => Math.min(roundLogsTotalPages, prev + 1))}
                        disabled={roundLogsPage === roundLogsTotalPages}
                        className="flex items-center gap-1"
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="performance_logs">
          <Card className="shadow-lg rounded-lg bg-card mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="dark:text-white light:text-gray-800">
                Performance Logs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={perfTab} onValueChange={v => setPerfTab(v as any)} className="mb-4">
                <TabsList className="mb-4">
                  <TabsTrigger value="debater">Debater</TabsTrigger>
                  <TabsTrigger value="adjudicator">Adjudicator</TabsTrigger>
                  <TabsTrigger value="total">Total Compute</TabsTrigger>
                </TabsList>
                <TabsContent value="debater">
                  {usersLoading ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">Loading users...</div>
                  ) : selectedUser && userPerfStats ? (
                    <div>
                      <Button variant="outline" onClick={() => setSelectedUser(null)} className="mb-4">Back to User List</Button>
                      <div className="flex items-center gap-3 mb-4">
                        <UserAvatar username={selectedUser.username} size="md" />
                        <div>
                          <div className="font-bold">{selectedUser.username}</div>
                          <div className="text-xs text-gray-500">Debater</div>
                        </div>
                      </div>
                      {/* Debater stats summary */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                        <Card><CardContent>...</CardContent></Card>
                        <Card><CardContent>...</CardContent></Card>
                        <Card><CardContent>...</CardContent></Card>
                      </div>
                      {/* Debater stats progression */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                        <div className="h-80 overflow-x-auto p-2 bg-transparent rounded-lg">
                          <h4 className="font-semibold mb-2">Speaker Score Progression (Line Graph)</h4>
                          <Line
                            data={{
                              labels: userPerfStats.debate_stats.score_progression?.labels || [],
                              datasets: [
                                {
                                  label: selectedUser.username,
                                  data: userPerfStats.debate_stats.score_progression?.scores || [],
                                  borderColor: 'rgba(53, 162, 235, 0.7)',
                                  backgroundColor: 'rgba(53, 162, 235, 0.2)',
                                  pointStyle: 'circle',
                                  pointRadius: 5,
                                  pointHoverRadius: 7,
                                },
                              ],
                            }}
                            options={{
                              plugins: { legend: { display: false } },
                              responsive: true,
                              scales: {
                                y: { min: 67, max: 83, ticks: { stepSize: 5 }, title: { display: true, text: 'Speaker Score' } },
                                x: { title: { display: true, text: 'Round' } },
                              },
                            }}
                          />
                        </div>
                        <div className="h-80 overflow-x-auto p-2 bg-transparent rounded-lg">
                          <h4 className="font-semibold mb-2">Role Distribution (Pie Chart)</h4>
                          <Pie
                            data={{
                              labels: Object.keys(userPerfStats.debate_stats.roles_distribution || {}),
                              datasets: [
                                {
                                  label: 'Roles',
                                  data: Object.values(userPerfStats.debate_stats.roles_distribution || {}),
                                  backgroundColor: [
                                    'rgba(255, 99, 132, 0.7)',
                                    'rgba(54, 162, 235, 0.7)',
                                    'rgba(255, 206, 86, 0.7)',
                                    'rgba(75, 192, 192, 0.7)',
                                    'rgba(153, 102, 255, 0.7)',
                                    'rgba(255, 159, 64, 0.7)',
                                    'rgba(255, 129, 102, 0.7)',
                                    'rgba(56, 198, 139, 0.7)',
                                  ],
                                },
                              ],
                            }}
                            options={{
                              plugins: { legend: { display: true, position: 'bottom' } },
                              responsive: true,
                            }}
                          />
                        </div>
                      </div>
                      {/* Speaking Time Progression */}
                      <div className="h-80 overflow-x-auto p-2 bg-transparent rounded-lg">
                        <h4 className="font-semibold mb-2">Speaking Time Progression (Line Graph)</h4>
                        <Line
                          data={{
                            labels: userPerfStats.debate_stats.speaking_time_progression?.labels || [],
                            datasets: [
                              {
                                label: selectedUser.username,
                                data: (userPerfStats.debate_stats.speaking_time_progression?.seconds || []).map((s: number) => s / 60),
                                borderColor: 'rgba(75, 192, 192, 0.7)',
                                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                                pointStyle: 'circle',
                                pointRadius: 5,
                                pointHoverRadius: 7,
                              },
                            ],
                          }}
                          options={{
                            plugins: { legend: { display: false } },
                            responsive: true,
                            scales: {
                              y: {
                                min: 0,
                                max: 8,
                                ticks: {
                                  stepSize: 0.5,
                                  callback: function(tickValue: string | number) {
                                    if (typeof tickValue === 'number') {
                                      return formatSecondsToMinutes(tickValue * 60);
                                    }
                                    return tickValue;
                                  }
                                },
                                title: { display: true, text: 'Speaking Time (minutes)' },
                              },
                              x: { title: { display: true, text: 'Round' } },
                            },
                          }}
                        />
                      </div>
                    </div>
                  ) : selectedUser && userPerfLoading ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">Loading user performance...</div>
                  ) : selectedUser && userPerfError ? (
                    <div className="p-4 text-center text-red-500">{userPerfError}</div>
                  ) : (
                    <div>
                      <h2 className="admin-logs-heading text-lg font-semibold mb-4">All Users</h2>
                      <div className="overflow-x-auto">
                        <table className="min-w-[600px] w-full text-sm">
                          <thead>
                            <tr>
                              <th className="p-2 text-left">User</th>
                              <th className="p-2 text-left">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedUsersList.map(user => (
                              <tr key={user.id}>
                                <td className="p-2 flex items-center gap-2"><UserAvatar username={user.username} size="sm" />{user.username}</td>
                                <td className="p-2">
                                  <Button size="sm" variant="secondary" onClick={() => setSelectedUser(user)}>View</Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {usersListTotalPages > 1 && (
                        <div className="flex justify-center items-center gap-2 mt-6">
                          <Button
                            variant="outline"
                            onClick={() => setPerformancePage(prev => Math.max(1, prev - 1))}
                            disabled={performancePage === 1}
                            className="flex items-center gap-1"
                          >
                            Previous
                          </Button>
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            Page {performancePage} of {usersListTotalPages}
                          </span>
                          <Button
                            variant="outline"
                            onClick={() => setPerformancePage(prev => Math.min(usersListTotalPages, prev + 1))}
                            disabled={performancePage === usersListTotalPages}
                            className="flex items-center gap-1"
                          >
                            Next
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="adjudicator">
                  {usersLoading ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">Loading users...</div>
                  ) : selectedUser && userPerfStats ? (
                    <div>
                      <Button variant="outline" onClick={() => setSelectedUser(null)} className="mb-4">Back to User List</Button>
                      <div className="flex items-center gap-3 mb-4">
                        <UserAvatar username={selectedUser.username} size="md" />
                        <div>
                          <div className="font-bold">{selectedUser.username}</div>
                          <div className="text-xs text-gray-500">Adjudicator</div>
                        </div>
                      </div>
                      {/* Adjudicator stats summary */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                        <Card><CardContent>...</CardContent></Card>
                        <Card><CardContent>...</CardContent></Card>
                        <Card><CardContent>...</CardContent></Card>
                      </div>
                      {/* Adjudicator stats progression */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                        <div className="h-80 overflow-x-auto p-2 bg-transparent rounded-lg">
                          <h4 className="font-semibold mb-2">Adjudicator Rating Progression (Line Graph)</h4>
                          <Line
                            data={{
                              labels: userPerfStats.adjudication_stats.rating_progression?.labels || [],
                              datasets: [
                                {
                                  label: selectedUser.username,
                                  data: userPerfStats.adjudication_stats.rating_progression?.ratings || [],
                                  borderColor: 'rgba(255, 159, 64, 0.7)',
                                  backgroundColor: 'rgba(255, 159, 64, 0.2)',
                                  pointStyle: 'circle',
                                  pointRadius: 5,
                                  pointHoverRadius: 7,
                                },
                              ],
                            }}
                            options={{
                              plugins: { legend: { display: false } },
                              responsive: true,
                              scales: {
                                y: { min: 1, max: 10, ticks: { stepSize: 1 }, title: { display: true, text: 'Adjudicator Rating' } },
                                x: { title: { display: true, text: 'Round' } },
                              },
                            }}
                          />
                        </div>
                        <div className="h-80 overflow-x-auto p-2 bg-transparent rounded-lg">
                          <h4 className="font-semibold mb-2">Role Distribution (Bar Chart)</h4>
                          <Bar
                            data={{
                              labels: Object.keys(userPerfStats.adjudication_stats.roles_distribution || {}),
                              datasets: [
                                {
                                  label: 'Roles',
                                  data: Object.values(userPerfStats.adjudication_stats.roles_distribution || {}),
                                  backgroundColor: [
                                    'rgba(255, 159, 64, 0.7)',
                                    'rgba(54, 162, 235, 0.7)',
                                    'rgba(153, 102, 255, 0.7)',
                                  ],
                                },
                              ],
                            }}
                            options={{
                              plugins: { legend: { display: false } },
                              responsive: true,
                              indexAxis: 'y',
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ) : selectedUser && userPerfLoading ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">Loading user performance...</div>
                  ) : selectedUser && userPerfError ? (
                    <div className="p-4 text-center text-red-500">{userPerfError}</div>
                  ) : (
                    <div>
                      <h2 className="admin-logs-heading text-lg font-semibold mb-4">All Users</h2>
                      <div className="overflow-x-auto">
                        <table className="min-w-[600px] w-full text-sm">
                          <thead>
                            <tr>
                              <th className="p-2 text-left">User</th>
                              <th className="p-2 text-left">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedUsersList.map(user => (
                              <tr key={user.id}>
                                <td className="p-2 flex items-center gap-2"><UserAvatar username={user.username} size="sm" />{user.username}</td>
                                <td className="p-2">
                                  <Button size="sm" variant="secondary" onClick={() => setSelectedUser(user)}>View</Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {usersListTotalPages > 1 && (
                        <div className="flex justify-center items-center gap-2 mt-6">
                          <Button
                            variant="outline"
                            onClick={() => setPerformancePage(prev => Math.max(1, prev - 1))}
                            disabled={performancePage === 1}
                            className="flex items-center gap-1"
                          >
                            Previous
                          </Button>
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            Page {performancePage} of {usersListTotalPages}
                          </span>
                          <Button
                            variant="outline"
                            onClick={() => setPerformancePage(prev => Math.min(usersListTotalPages, prev + 1))}
                            disabled={performancePage === usersListTotalPages}
                            className="flex items-center gap-1"
                          >
                            Next
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="total">
                  {totalComputeLoading ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">Loading total compute stats...</div>
                  ) : totalComputeStats.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">No data available for total compute.</div>
                  ) : (
                    <div>
                      <h2 className="admin-logs-heading text-lg font-semibold mb-4">Total Compute (All Users)</h2>
                      <div className="overflow-x-auto mb-6">
                        <table className="min-w-[600px] w-full text-sm">
                          <thead>
                            <tr>
                              <th className="p-2 text-left">User</th>
                              <th className="p-2 text-left">Wins</th>
                              <th className="p-2 text-left">Speaker Score</th>
                              <th className="p-2 text-left">Adjudicator Rating</th>
                              <th className="p-2 text-left">Speaking Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            {totalComputeStats.filter(entry => entry && entry.username && entry.debate_stats && entry.adjudication_stats).map((entry) => (
                              <tr key={entry.user_id}>
                                <td className="p-2 flex items-center gap-2"><UserAvatar username={entry.username} size="sm" />{entry.username}</td>
                                <td className="p-2">{entry.debate_stats?.wins || 0}</td>
                                <td className="p-2">{entry.debate_stats?.avg_score?.toFixed(2) || '-'}</td>
                                <td className="p-2">{entry.adjudication_stats?.avg_rating?.toFixed(2) || '-'}</td>
                                <td className="p-2">{entry.debate_stats?.speaking_time || 0}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {/* Line graph comparing users by speaker score progression */}
                      <div className="h-80 overflow-x-auto p-2 bg-transparent rounded-lg">
                        <h4 className="font-semibold mb-2">Speaker Score Progression (Line Graph)</h4>
                        <Line
                          data={{
                            labels: (() => {
                              const allLabelsScore = totalComputeStats.filter(e => e && e.username && e.debate_stats && e.debate_stats.score_progression?.labels).flatMap((entry) => entry.debate_stats.score_progression?.labels || []);
                              return Array.from(new Set(allLabelsScore));
                            })(),
                            datasets: totalComputeStats.filter(e => e && e.username && e.debate_stats && e.debate_stats.score_progression?.labels).map((entry) => ({
                              label: entry.username,
                              data: (() => {
                                const labels = entry.debate_stats.score_progression?.labels || [];
                                const scores = entry.debate_stats.score_progression?.scores || [];
                                const allLabelsScore = totalComputeStats.filter(e => e && e.username && e.debate_stats && e.debate_stats.score_progression?.labels).flatMap((entry) => entry.debate_stats.score_progression?.labels || []);
                                return Array.from(new Set(allLabelsScore)).map(lab => {
                                  const idx = labels.indexOf(lab);
                                  return idx !== -1 ? scores[idx] : null;
                                });
                              })(),
                              borderColor: 'rgba(53, 162, 235, 0.7)',
                              backgroundColor: 'rgba(53, 162, 235, 0.2)',
                              pointStyle: 'circle',
                              pointRadius: 5,
                              pointHoverRadius: 7,
                            }))
                          }}
                          options={{
                            plugins: {
                              tooltip: {
                                callbacks: {
                                  label: function(context: any) {
                                    const username = context.dataset.label;
                                    const avatarUrl = totalComputeAvatarMap[username];
                                    return [
                                      `User: ${username}`,
                                      avatarUrl ? `Avatar: ${avatarUrl}` : '',
                                      `Score: ${context.parsed.y}`
                                    ];
                                  }
                                }
                              },
                              legend: {
                                labels: {
                                  generateLabels: function(chart) {
                                    // Custom legend with avatars
                                    const datasets = chart.data.datasets || [];
                                    return datasets.map((ds: any, i: number) => ({
                                      text: ds.label,
                                      fillStyle: ds.borderColor,
                                      strokeStyle: ds.borderColor,
                                      lineWidth: 2,
                                      hidden: !chart.isDatasetVisible(i),
                                      index: i,
                                      // Add avatar URL for custom rendering if needed
                                      avatarUrl: totalComputeAvatarMap[ds.label] || '',
                                    }));
                                  }
                                }
                              }
                            },
                            responsive: true,
                            interaction: { mode: 'nearest', intersect: false },
                            scales: {
                              y: { min: 67, max: 83, ticks: { stepSize: 2 }, title: { display: true, text: 'Speaker Score' } },
                              x: { title: { display: true, text: 'Round' } }
                            }
                          }}
                        />
                      </div>
                      {/* Line graph comparing users by adjudicator rating progression */}
                      <div className="h-80 overflow-x-auto p-2 bg-transparent rounded-lg">
                        <h4 className="font-semibold mb-2">Adjudicator Rating Progression (Line Graph)</h4>
                        <Line
                          data={{
                            labels: (() => {
                              const allLabelsRating = totalComputeStats.filter(e => e && e.username && e.adjudication_stats && e.adjudication_stats.rating_progression?.labels).flatMap((entry) => entry.adjudication_stats.rating_progression?.labels || []);
                              return Array.from(new Set(allLabelsRating));
                            })(),
                            datasets: totalComputeStats.filter(e => e && e.username && e.adjudication_stats && e.adjudication_stats.rating_progression?.labels).map((entry) => ({
                              label: entry.username,
                              data: (() => {
                                const labels = entry.adjudication_stats.rating_progression?.labels || [];
                                const ratings = entry.adjudication_stats.rating_progression?.ratings || [];
                                const allLabelsRating = totalComputeStats.filter(e => e && e.username && e.adjudication_stats && e.adjudication_stats.rating_progression?.labels).flatMap((entry) => entry.adjudication_stats.rating_progression?.labels || []);
                                return Array.from(new Set(allLabelsRating)).map(lab => {
                                  const idx = labels.indexOf(lab);
                                  return idx !== -1 ? ratings[idx] : null;
                                });
                              })(),
                              borderColor: 'rgba(255, 159, 64, 0.7)',
                              backgroundColor: 'rgba(255, 159, 64, 0.2)',
                              pointStyle: 'circle',
                              pointRadius: 5,
                              pointHoverRadius: 7,
                            }))
                          }}
                          options={{
                            plugins: {
                              tooltip: {
                                callbacks: {
                                  label: function(context: any) {
                                    const username = context.dataset.label;
                                    const avatarUrl = totalComputeAvatarMap[username];
                                    return [
                                      `User: ${username}`,
                                      avatarUrl ? `Avatar: ${avatarUrl}` : '',
                                      `Rating: ${context.parsed.y}`
                                    ];
                                  }
                                }
                              },
                              legend: {
                                labels: {
                                  generateLabels: function(chart) {
                                    const datasets = chart.data.datasets || [];
                                    return datasets.map((ds: any, i: number) => ({
                                      text: ds.label,
                                      fillStyle: ds.borderColor,
                                      strokeStyle: ds.borderColor,
                                      lineWidth: 2,
                                      hidden: !chart.isDatasetVisible(i),
                                      index: i,
                                      avatarUrl: totalComputeAvatarMap[ds.label] || '',
                                    }));
                                  }
                                }
                              }
                            },
                            responsive: true,
                            interaction: { mode: 'nearest', intersect: false },
                            scales: {
                              y: { min: 1, max: 10, ticks: { stepSize: 1 }, title: { display: true, text: 'Adjudicator Rating' } },
                              x: { title: { display: true, text: 'Round' } }
                            }
                          }}
                        />
                      </div>
                      {/* Line graph comparing users by speaking time progression */}
                      <div className="h-80 overflow-x-auto p-2 bg-transparent rounded-lg">
                        <h4 className="font-semibold mb-2">Speaking Time Progression (Line Graph)</h4>
                        <Line
                          data={{
                            labels: (() => {
                              const allLabelsSpeaking = totalComputeStats.filter(e => e && e.username && e.debate_stats && e.debate_stats.speaking_time_progression?.labels).flatMap((entry) => entry.debate_stats.speaking_time_progression?.labels || []);
                              return Array.from(new Set(allLabelsSpeaking));
                            })(),
                            datasets: totalComputeStats.filter(e => e && e.username && e.debate_stats && e.debate_stats.speaking_time_progression?.labels).map((entry) => ({
                              label: entry.username,
                              data: (() => {
                                const labels = entry.debate_stats.speaking_time_progression?.labels || [];
                                const seconds = entry.debate_stats.speaking_time_progression?.seconds || [];
                                const allLabelsSpeaking = totalComputeStats.filter(e => e && e.username && e.debate_stats && e.debate_stats.speaking_time_progression?.labels).flatMap((entry) => entry.debate_stats.speaking_time_progression?.labels || []);
                                return Array.from(new Set(allLabelsSpeaking)).map(lab => {
                                  const idx = labels.indexOf(lab);
                                  return idx !== -1 ? seconds[idx] / 60 : null;
                                });
                              })(),
                              borderColor: 'rgba(75, 192, 192, 0.7)',
                              backgroundColor: 'rgba(75, 192, 192, 0.2)',
                              pointStyle: 'circle',
                              pointRadius: 5,
                              pointHoverRadius: 7,
                            }))
                          }}
                          options={{
                            plugins: {
                              tooltip: {
                                callbacks: {
                                  label: function(context: any) {
                                    const username = context.dataset.label;
                                    const avatarUrl = totalComputeAvatarMap[username];
                                    return [
                                      `User: ${username}`,
                                      avatarUrl ? `Avatar: ${avatarUrl}` : '',
                                      `Speaking Time: ${formatSecondsToMinutes(context.parsed.y * 60)}`
                                    ];
                                  }
                                }
                              },
                              legend: {
                                labels: {
                                  generateLabels: function(chart) {
                                    const datasets = chart.data.datasets || [];
                                    return datasets.map((ds: any, i: number) => ({
                                      text: ds.label,
                                      fillStyle: ds.borderColor,
                                      strokeStyle: ds.borderColor,
                                      lineWidth: 2,
                                      hidden: !chart.isDatasetVisible(i),
                                      index: i,
                                      avatarUrl: totalComputeAvatarMap[ds.label] || '',
                                    }));
                                  }
                                }
                              }
                            },
                            responsive: true,
                            interaction: { mode: 'nearest', intersect: false },
                            scales: {
                              y: {
                                min: 0,
                                max: 8,
                                ticks: {
                                  stepSize: 0.5,
                                  callback: function(tickValue: string | number) {
                                    if (typeof tickValue === 'number') {
                                      return formatSecondsToMinutes(tickValue * 60);
                                    }
                                    return tickValue;
                                  }
                                },
                                title: { display: true, text: 'Speaking Time (minutes)' },
                              },
                              x: { title: { display: true, text: 'Round' } }
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminLogsPanel;